import { Box, ButtonBase, Stack, useTheme } from "@mui/material";
import { AiPoweredChipWrapper } from "@src/components/Dialogs/CreateExperienceDialog/AiPoweredChipWrapper";
import { IgBluePrimitives } from "@intelligems/ig-design-tokens";
import { IgTooltip, IgTypography } from "@intelligems/ig-theme";
import { ReactNode } from "react";
import { ExperienceCreationCard } from "@src/components/Dialogs/CreateExperienceDialog/types";
import { ExperienceCardGraphic } from "@src/components/Dialogs/CreateExperienceDialog/ExperienceCardGraphic";

export const ExperienceTypeCard = ({
  card,
  disabledReason,
  onSelect,
}: {
  card: ExperienceCreationCard;
  disabledReason?: ReactNode;
  onSelect: (card: ExperienceCreationCard) => void;
}) => {
  const theme = useTheme();
  const isDisabled = !!disabledReason;
  const cardContent = (
    <ButtonBase
      disabled={isDisabled}
      onClick={() => onSelect(card)}
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "stretch",
        width: 260,
        height: 198,
        border: `1px solid ${theme.igBorders.core.disabled}`,
        borderRadius: "6px",
        overflow: "hidden",
        textAlign: "left",
        opacity: isDisabled ? 0.55 : 1,
        cursor: isDisabled ? "not-allowed" : "pointer",
        backgroundColor: theme.igBackgroundColors.baseLightHigh,
        "&:hover": {
          borderColor: isDisabled
            ? theme.igBorders.core.disabled
            : theme.igBorders.core.hover,
        },
      }}
    >
      {card.isAiPowered ? (
        <AiPoweredChipWrapper>
          <ExperienceCardGraphic kind={card.graphic} />
        </AiPoweredChipWrapper>
      ) : (
        <ExperienceCardGraphic kind={card.graphic} />
      )}

      <Stack
        spacing={0.5}
        sx={{
          flex: 1,
          width: "100%",
          px: 3,
          py: "10px",
          backgroundColor: theme.igBackgroundColors.baseLightHigh,
          boxShadow: theme.igShadows.low,
        }}
      >
        <Stack
          direction="row"
          spacing={2}
          alignItems="center"
          sx={{ overflow: "hidden", width: "100%" }}
        >
          <IgTypography fontWeight={600} noWrap sx={{ minWidth: 0 }}>
            {card.title}
          </IgTypography>
          {card?.badgeContent && (
            <Stack
              justifyContent="center"
              sx={{
                flexShrink: 0,
                px: "4px",
                py: "2px",
                borderRadius: "4px",
                backgroundColor: IgBluePrimitives[100],
              }}
            >
              <IgTypography
                variant="body3"
                fontWeight={600}
                sx={{
                  whiteSpace: "nowrap",
                  color: IgBluePrimitives.A200,
                }}
              >
                {card.badgeContent}
              </IgTypography>
            </Stack>
          )}
        </Stack>
        <IgTypography variant="body2" color={theme.palette.text.tertiary}>
          {card.description}
        </IgTypography>
      </Stack>
    </ButtonBase>
  );

  if (!disabledReason) return cardContent;

  return (
    <IgTooltip title={disabledReason} placement="top">
      <Box>{cardContent}</Box>
    </IgTooltip>
  );
};
